import { getTaxRate, printNumber, splitDate, yearRange } from "src/utils";
import { jointTable, makeTable } from "../Longevity/calculate";
import { useFullscreen } from "src/hooks/useFullScreen";
import { calculateSpendingYear } from "../Spending/calculate";
import { Client, ScenarioSettings, SelectedColumn } from "src/types";
import { useScenario } from "src/hooks/useScenario";
import { useUser } from "src/hooks/useUser";

interface Props {
  scenario: ScenarioSettings;
  client: Client;
  selectedYear?: number;
  selectedColumn?: SelectedColumn;
  setSelectedColumn?: any;
  setSelectedYear?: any;
  print?: boolean;
}

const CompositeTable = ({
  scenario,
  client,
  setSelectedYear,
  selectedYear,
  setSelectedColumn,
  selectedColumn,
  print,
}: Props) => {
  const currentYear = new Date().getFullYear();
  const { isFullscreen } = useFullscreen();
  const { scenario: calculated } = useScenario();
  const { user } = useUser();

  const tables = client.people.map((person) => makeTable(person));
  const joint =
    client.people.length > 1
      ? jointTable(client.people[0], client.people[1])
      : [];

  const setColumn = (name: string) => () => {
    console.log(name);
    selectedColumn?.type === name
      ? setSelectedColumn({ type: "none", id: 0 })
      : setSelectedColumn({ type: name, id: 0 });
  };
  const setRow = (year: number) => () => {
    selectedYear === year ? setSelectedYear(-1) : setSelectedYear(year);
  };

  const divisionFactor =
    client.liveSettings.monthlyYearly === "monthly" ? 12 : 1;
  return (
    <div className="flex justify-between flex-wrap">
      <div className="w-full">
        <table className="border print:border-none bg-white text-sm! w-full">
          <thead
            className={`text-xs cursor-pointer bg-[#F9FAFB] text-black  text-left sticky z-50 border ${isFullscreen ? "top-[111px]" : !print && "top-[271px]"} border-separate print:top-0! print:border-none print:border-b-gray-500 print:border-b-2  print:font-bold`}
          >
            <tr>
              <th
                className={`px-6 py-[0.45rem] font-medium ${selectedColumn?.type === "year" ? "bg-slate-200!" : ""}`}
                onClick={setColumn("year")}
              >
                Year
              </th>
              <th
                className={` font-medium ${selectedColumn?.type === "age" ? "bg-slate-200!" : ""} border-r border-gray-700 border-solid`}
                onClick={setColumn("age")}
              >
                <div className="px-2 h-full w-full">Age</div>
              </th>

              {user?.info?.longevityFlag &&
                client.people.every((p) => p.sex) &&
                client.people.map((person, i) => (
                  <th
                    className={`px-2 py-[0.45rem] text-center font-medium ${selectedColumn?.type === `${i}-alive` ? "bg-slate-200!" : ""} ${client.people.length == 1 ? "border-r border-gray-700" : ""}`}
                    onClick={setColumn(`${i}-alive`)}
                  >
                    {person.name} <br /> alive
                  </th>
                ))}
              {user?.info?.longevityFlag &&
                client.people.every((p) => p.sex) &&
                client.people.length > 1 && (
                  <th
                    className={`px-2 py-[0.45rem] text-center font-medium ${selectedColumn?.type === "joint-alive" ? "bg-slate-200!" : ""} ${client.people.length > 1 ? "border-r border-gray-700" : ""}`}
                    onClick={setColumn("joint-alive")}
                  >
                    At least one <br /> alive
                  </th>
                )}
              <th
                className={`px-2 font-medium ${selectedColumn?.type === "total" ? "bg-slate-200!" : ""}`}
                onClick={setColumn("total")}
              >
                Income
              </th>

              {user?.info?.taxesFlag && scenario.taxType == "Post" && (
                <th
                  className={`px-2 font-medium  ${selectedColumn?.type === "taxes" ? "bg-slate-200!" : ""}`}
                  onClick={setColumn("taxes")}
                >
                  Taxes
                </th>
              )}

              {user?.info?.taxesFlag && scenario.taxType == "Post" && (
                <th
                  className={`px-2 font-medium  ${selectedColumn?.type === "posttax" ? "bg-slate-200!" : ""} ${!user.info?.needsFlag && user.info?.stabilityRatioFlag && "border-r border-gray-700"}`}
                  onClick={setColumn("posttax")}
                >
                  Post-Tax Income
                </th>
              )}

              {user?.info?.needsFlag && (
                <th
                  className={`px-2 font-medium ${selectedColumn?.type === "spending" ? "bg-slate-200!" : ""}`}
                  onClick={setColumn("spending")}
                >
                  Spending
                </th>
              )}
              {user?.info?.needsFlag && (
                <th
                  className={`px-2 font-medium border-r border-gray-700  ${selectedColumn?.type === "gap" ? "bg-slate-200!" : ""}`}
                  onClick={setColumn("gap")}
                >
                  Gap
                </th>
              )}
              {user?.info?.stabilityRatioFlag && (
                <th
                  className={`px-2  font-medium text-center ${selectedColumn?.type === "income-stability" ? "bg-slate-200!" : ""}`}
                  onClick={setColumn("income-stability")}
                >
                  Income <br /> Stability
                </th>
              )}
              {user?.info?.stabilityRatioFlag && user.info?.needsFlag && (
                <th
                  className={`px-2 font-medium text-center py-[0.45rem] ${selectedColumn?.type === "spending-stability" ? "bg-slate-200!" : ""}`}
                  onClick={setColumn("spending-stability")}
                >
                  Spending <br /> Stability
                </th>
              )}
            </tr>
          </thead>
          <tbody className="print:text-sm inconsolata">
            {yearRange(
              currentYear,
              currentYear + scenario.maxYearsShown - 1,
            ).map((line, index) => {
              const year = calculated[line];
              if (!year) return null;
              const needs =
                calculateSpendingYear(
                  client.people,
                  client.spending,
                  scenario,
                  line,
                ) / divisionFactor;

              const income = scenario.incomes
                .filter((income) => income.enabled)
                .map((income) => year.incomes[income.id]?.amount)
                .filter((t) => typeof t === "number")
                .reduce((a, b) => a + b, 0);

              const stableIncome = scenario.incomes
                .filter((item) => item.stable)
                .filter((income) => income.enabled)
                .map((income) => year.incomes[income.id]?.amount)
                .filter((t) => typeof t === "number")
                .reduce((a, b) => a + b, 0);

              if (line === 2036) {
                console.log("stableIncome", stableIncome);
                console.log("income", income);
                console.log(
                  "si",
                  scenario.incomes
                    .filter((item) => item.stable)
                    .filter((income) => income.enabled)
                    .map((income) => year.incomes[income.id]?.amount),
                );
                console.log(
                  "total",
                  scenario.incomes
                    .filter((income) => income.enabled)
                    .map((income) => ({
                      id: income.id,
                      type: income.type,
                      amount: year.incomes[income.id]?.amount,
                    })),
                );
              }

              const stabilityRatio = Math.round((stableIncome / income) * 100);

              const taxRate = getTaxRate(user, scenario, line);

              const spendingStability = Math.round(
                ((stableIncome * (1 - taxRate)) / needs) * 100,
              );

              const taxes = income * taxRate;

              const gap = income - needs - taxes;
              return (
                <tr
                  className={`${index % 2 == 1 ? "bg-[#F9FAFB]" : "bg-white"} border-y border-[#EAECF0] ${selectedYear === line ? "bg-slate-200!" : ""}`}
                  onClick={setRow(line)}
                >
                  <td
                    className={`px-6 py-[6px] print:py-[0.2rem] font-medium  print:font-medium ${selectedColumn?.type === "year" ? "bg-slate-200!" : ""}`}
                  >
                    {line}
                  </td>
                  <td
                    className={`px-2 py-[0.45rem] print:py-[0.2rem] font-medium border-r border-gray-700 ${selectedColumn?.type === "age" ? "bg-slate-200!" : ""}`}
                  >
                    {client.people
                      .map((p) => line - splitDate(p.birthday).year)
                      .join("/")}
                  </td>

                  {user?.info?.longevityFlag &&
                    client.people.every((p) => p.sex) &&
                    client.people.map((_, i) => (
                      <td
                        className={`px-2 py-[6px] print:py-[0.2rem] text-center ${selectedColumn?.type === `${i}-alive` ? "bg-slate-200!" : ""} ${client.people.length == 1 ? "border-r border-gray-700" : ""}`}
                      >
                        {Math.round(
                          (tables[i].table.find((entry) => entry.year === line)
                            ?.probability || 0) * 100,
                        )}
                        %
                      </td>
                    ))}
                  {user?.info?.longevityFlag &&
                    client.people.every((p) => p.sex) &&
                    client.people.length > 1 && (
                      <td
                        className={`px-2 py-[6px] print:py-[0.2rem] text-center ${selectedColumn?.type === `joint-alive` ? "bg-slate-200!" : ""} ${client.people.length > 1 ? "border-r border-gray-700" : ""}`}
                      >
                        {Math.round(
                          (joint.find((entry) => entry.year === line)
                            ?.oneAlive || 0) * 100,
                        )}
                        %
                      </td>
                    )}
                  <td
                    className={`px-2 py-1 print:py-[0.2rem] ${selectedColumn?.type === "total" ? "bg-slate-200!" : ""}`}
                  >
                    {printNumber(income)}
                  </td>

                  {user?.info?.taxesFlag && scenario.taxType == "Post" && (
                    <td
                      className={`px-2 py-1 print:py-[0.2rem] ${selectedColumn?.type === "taxes" ? "bg-slate-200!" : ""}`}
                    >
                      {printNumber(taxes)}
                    </td>
                  )}

                  {user?.info?.taxesFlag && scenario.taxType == "Post" && (
                    <td
                      className={`px-2 py-1 print:py-[0.2rem] ${selectedColumn?.type === "posttax" ? "bg-slate-200!" : ""}  ${!user.info?.needsFlag && user.info?.stabilityRatioFlag && "border-r border-gray-700"}`}
                    >
                      {printNumber(income - taxes)}
                    </td>
                  )}
                  {user?.info?.needsFlag && (
                    <td
                      className={`px-2 py-1 print:py-[0.2rem] ${selectedColumn?.type === "spending" ? "bg-slate-200!" : ""}`}
                    >
                      {printNumber(needs)}
                    </td>
                  )}
                  {user?.info?.needsFlag && (
                    <td
                      className={`px-2 py-1 print:py-[0.2rem] border-r border-gray-700 ${gap >= 0 ? "text-green-500" : "text-red-500"} ${selectedColumn?.type === "gap" ? "bg-slate-200!" : ""}`}
                    >
                      {printNumber(gap)}
                    </td>
                  )}
                  {user?.info?.stabilityRatioFlag && (
                    <td
                      className={`px-2 py-[6px] print:py-[0.2rem] text-center ${selectedColumn?.type === `income-stability` ? "bg-slate-200!" : ""}`}
                    >
                      {!isNaN(stabilityRatio) ? `${stabilityRatio}%` : "N/A"}
                    </td>
                  )}
                  {user?.info?.stabilityRatioFlag && user.info?.needsFlag && (
                    <td
                      className={`px-2 py-[6px] print:py-[0.2rem] text-center ${selectedColumn?.type === `spending-stability` ? "bg-slate-200!" : ""}`}
                    >
                      {!isNaN(spendingStability) &&
                      spendingStability !== Infinity
                        ? `${spendingStability}%`
                        : "N/A"}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompositeTable;
